import { useId, useState } from "react";
import type { BoyaRaporuOzeti } from "@oasis/domain";
import { Buton, Cip, Etiket, Modal, etiket } from "@/components/parcalar";
import { BOS_SAYFA, SayfaAlani, belgeLinki, sayfaAlanlari, sayfaEtiketi, sayfaGecerli, type SayfaSecimi } from "@/components/pdf-sayfa";
import { api } from "@/lib/api";
import { cevir } from "@/lib/dil";
import { boyaEtkileri, boyaFinalAlanlari, boyaKaldirOnayi, finalYapIstegi, finalYapOnayi, type BoyaFinalSecimi, type BoyaMevcutFinal } from "@/lib/boya-raporu";
import { dosyaSec, tarihSaat } from "@/lib/veri";

export type { BoyaMevcutFinal };

/**
 * D-074 Faz 5 · PR 24 (§5.12, K17): "Final coating report" sorusu. Finali kişi seçer, OASIS atamaz: iki seçenek seçimsiz açılır
 * (K7'deki "reject" ipucunun boş açılması emsali) ve seçim yapılmadan yüklenmez / dosyalanmaz. Finishing'in "Upload Coating report"
 * penceresi ve Documents'ın boya kartı aynı alanı kullanır.
 */
export function BoyaFinalAlani({ deger, onChange, disabled, etiketsiz }: { deger: BoyaFinalSecimi; onChange: (s: BoyaFinalSecimi) => void; disabled?: boolean; etiketsiz?: boolean }) {
  const isim = useId();
  return (
    <fieldset className="mt-3" disabled={disabled}>
      <legend className={etiketsiz ? "sr-only" : etiket}>{cevir("Final coating report")}</legend>
      <label className="flex items-start gap-2 text-sm"><input type="radio" name={isim} className="mt-0.5" checked={deger === "evet"} onChange={() => onChange("evet")} /> <span>{cevir("Final: Riser Documentation 3.6 and maintenance completion use this report")}</span></label>
      <label className="mt-1 flex items-start gap-2 text-sm"><input type="radio" name={isim} className="mt-0.5" checked={deger === "hayir"} onChange={() => onChange("hayir")} /> <span>{cevir("Not final: kept on record (primer, intermediate coat or an earlier report)")}</span></label>
      <p className="mt-1 text-xs text-ink-3">{cevir("Usually the final is the newest report that covers the riser (the topcoat). OASIS does not choose it.")}</p>
    </fieldset>
  );
}

/**
 * Finishing'in boya raporu yükleme penceresi (`HtYuklemePenceresi` kalıbı): dosya · Partial + sayfa (yalnız PDF; boya raporunda resim
 * de kabul) · "Final coating report" · etkiler · Upload · Cancel. Pencere sonuçla kapanır (`bitir`), yüklemeyi çağıran yapar. Final
 * seçiminde istek ekranın gördüğü finalin anahtarını `oncekiFinal` olarak taşır (`boyaFinalAlanlari`; final yoksa "").
 */
export function BoyaYuklemePenceresi({ baslik, rn, mevcutFinal, asmVar, bitir }: { baslik: string; rn: string; mevcutFinal: BoyaMevcutFinal; asmVar: boolean; bitir: (s: { dosya: File; alanlar: Record<string, string> } | null) => void }) {
  const [dosya, setDosya] = useState<File | null>(null);
  const [sayfa, setSayfa] = useState<SayfaSecimi>(BOS_SAYFA);
  const [secim, setSecim] = useState<BoyaFinalSecimi>("");
  const pdfMi = !dosya || /\.pdf$/i.test(dosya.name) || dosya.type === "application/pdf";
  const etkiler = boyaEtkileri({ rn, secim, mevcutFinalAd: mevcutFinal?.dosya ?? null, asmVar });
  return (
    <Modal baslik={baslik} onKapat={() => bitir(null)}>
      <div className="flex flex-wrap items-center gap-2">
        <Buton kucuk onClick={async () => { const f = await dosyaSec(".pdf,image/*"); if (f) setDosya(f); }}>{dosya ? cevir("Change file") : cevir("Select PDF")}</Buton>
        {dosya ? <Cip>📄 {dosya.name}</Cip> : <span className="text-sm text-ink-3">{cevir("not selected")}</span>}
      </div>
      {pdfMi && <SayfaAlani deger={sayfa} onChange={setSayfa} />}
      <BoyaFinalAlani deger={secim} onChange={setSecim} />
      {etkiler.length > 0 && (
        <div className="mt-3 rounded-md border border-line bg-surface-2 px-3 py-2 text-sm text-ink-2">
          <b>{cevir("Effects")}</b>
          <ul className="mt-1 list-disc pl-5">{etkiler.map((x) => <li key={x}>{x}</li>)}</ul>
        </div>
      )}
      <div className="mt-4 flex gap-2">
        <Buton
          tur="birincil"
          disabled={!dosya || (pdfMi && !sayfaGecerli(sayfa)) || secim === ""}
          onClick={() => {
            if (!dosya || secim === "") return;
            bitir({ dosya, alanlar: { ...(pdfMi ? sayfaAlanlari(sayfa) : {}), ...boyaFinalAlanlari(secim, mevcutFinal) } });
          }}
        >
          {cevir("Upload")}
        </Buton>
        <Buton onClick={() => bitir(null)}>{cevir("Cancel")}</Buton>
      </div>
    </Modal>
  );
}

/**
 * Finishing'in açılmış satırındaki boya raporu listesi (en yeniden eskiye). Final "FINAL" etiketini taşır; öteki raporlarda
 * "Set as final" (onay metni etkileri yazar). Her raporun kendi ✕'i yalnız o raporu kaldırır; final kaldırılırsa riser finalsiz kalır,
 * kalanlardan hiçbiri kendiliğinden final olmaz.
 */
export function BoyaRaporListesi({ rn, raporlar, mevcutFinal, asmVar, tamam, dene }: { rn: string; raporlar: readonly BoyaRaporuOzeti[]; mevcutFinal: BoyaMevcutFinal; asmVar: boolean; tamam: boolean; dene: (f: () => Promise<unknown>) => Promise<void> }) {
  if (!raporlar.length) return null;
  return (
    <div className="mb-2">
      <div className="pb-1 text-xs font-semibold text-ink-3">{cevir("Coating reports ({0}) · the final one counts for Riser Documentation 3.6 and maintenance completion", raporlar.length)}</div>
      {raporlar.map((r) => (
        <div key={r.id} className="flex flex-wrap items-center gap-2 border-b border-dashed border-line py-1.5 text-sm last:border-0">
          <a href={belgeLinki(r.dokumanId, r.sayfa)} target="_blank" rel="noreferrer"><Cip>🎨 {r.dosya}{r.sayfa ? ` · ${sayfaEtiketi(r.sayfa)}` : ""}</Cip></a>
          <span className="text-xs text-ink-3">{r.kim} · {tarihSaat(r.ts)}</span>
          {r.final
            ? <Etiket tone="ok">{cevir("FINAL")}</Etiket>
            : <Buton kucuk islem="evrak" onClick={() => { if (window.confirm(finalYapOnayi({ rn, ad: r.dosya, mevcutFinalAd: mevcutFinal?.dosya ?? null, asmVar }))) { const q = finalYapIstegi(rn, r.id, mevcutFinal); void dene(() => api.post(q.yol, q.govde)); } }}>{cevir("Set as final")}</Buton>}
          <Buton kucuk islem="evrak" title={cevir("Remove")} onClick={() => { if (window.confirm(boyaKaldirOnayi({ rn, ad: r.dosya, final: r.final, tamam }))) void dene(() => api.post(`/assembly/${rn}/boya/${r.id}/kaldir`)); }}>✕</Buton>
        </div>
      ))}
    </div>
  );
}
